<template>
  <div class="wide main-content">
    <div class="top-wrap">
      <div
        v-if="backgroundUrl"
        :style="backgroundUrl"
        class="clp-banner content-block"
      />
      <div class="top-content content-block">
        <h1>{{ pageData.title }}</h1>
        <div
          v-if="pageData.introText"
          v-dompurify-html="pageData.introText"
          class="intro-text-block"
        />
      </div>

      <count-down-block
        v-if="timerEnabled"
        :deadline="deadline"
        :title="timerTitle"
        :type="'green'"
        class="content-block"
      />

      <book-and-travel
        v-if="pageData.flightDetails.book.title || pageData.flightDetails.travel.title"
        :book="pageData.flightDetails.book"
        :travel="pageData.flightDetails.travel"
        class="content-block"
      />
    </div>

    <div class="content-block">
      <h2>{{ pageData.subTitle }}</h2>
      <div
        v-if="!isFiltersBlockHidden"
        class="search-form-wrap"
      >
        <div class="search-form">
          <div class="origin">
            <origin
              ref="origin"
              v-focusout="onOriginOpenUpdate"
              class="e-horizon-orig-destin clp-origin"
              :choose="$t('searchForm.originDestin.origin.originChooseEHorizon')"
              :class="[{ error: origin.invalid }]"
              :filter="origin.typeahead.filter"
              :is-mobile="deviceType"
              :label="$t('campaignLandingPage.filter.selectOrigin')"
              :open="origin.typeahead.open"
              :options="filteredAirports"
              :placeholder="$t('searchForm.originDestin.origin.placeholderEHorizon')"
              :selected-label="origin.selectedLabel"
              :touched="origin.typeaheadIgnoreFilter"
              :translation-no-results="$t('searchForm.originDestin.noResultsEHorizon')"
              :translation-invalid-language="wrongInputLangTranslation"
              :value="origin.value"
              @focused="showOriginTypeahead"
              @update:filter="onOriginFilter"
              @update:open="onOriginOpenUpdate"
              @update:label="onOriginLabelUpdate"
              @update:value="setOrigin"
            />
          </div>
          <div class="destin">
            <autocomplete-dropdown
              :items="destinList"
              :label="$t('campaignLandingPage.filter.searchByCityOrCountry')"
              :mobile-title="$t('campaignLandingPage.filter.searchByCityOrCountry')"
              :selected-item="selectedDestin"
              :can-show-all="true"
              class="destin-dropdown"
              @select="setDestin"
            />
          </div>
          <div class="sorter">
            <dropdown-simple
              :device-type="deviceType"
              :items="sorterOptions"
              :mobile-title="$t('campaignLandingPage.filter.sort')"
              :selected-item="selectedSorter"
              :selected-title="$t('campaignLandingPage.filter.sort')"
              @select="selectSorter"
            />
          </div>
        </div>
      </div>

      <histogram-legend />

      <div
        v-for="(category, key) in filteredFlights"
        :key="key"
        :class="{ closed: closedCategories[key], empty: !category.flights.length }"
        class="category"
      >
        <h2
          v-if="category.flights.length"
          @click="toggleCategory(key)"
        >
          {{ $t('campaignLandingPage.priceCategoryTitle') }} {{ category.price }}€
        </h2>
        <div
          v-show="!closedCategories[key]"
          class="flights-block"
        >
          <flight-item
            v-for="flight in category.flights"
            :key="`flight-${flight.originCode}-${flight.code}`"
            :flight="flight"
            :hide-images="hideImages"
            :icon-type="iconType"
            :is-ond="isOndCampaign"
            :open-all-histograms="openAllHistograms"
            :origin-code="flight.originCode"
            :parent-store="'campaignLandingPrices'"
            :trip-type="flight.tripType"
          />
        </div>
      </div>

      <div class="bottom-block">
        <terms-and-conditions
          v-if="termsAndConditions && termsAndConditions.content"
          :always-opened="true"
          :content="termsAndConditions.content"
          :title="termsAndConditions.title"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import ImageHelpers from 'utils/mixins/ImageHelpers'
  import Focusout from 'utils/directives/FocusOut'
  import OriginMixin from 'components/eHorizon/SearchFormMixins/Origin'
  import SearchForm from 'components/eHorizon/SearchFormMixins/SearchForm'

  export default {
    directives: {
      Focusout
    },
    components: {
      'count-down-block': () =>
        import(
          /* webpackChunkName: "count-down-block" */ 'containers/Pages/LandingPage/CountDownBlock'
        ),
      'dropdown-simple': () =>
        import(
          /* webpackChunkName: "dropdown-simple" */ 'components/Dropdown/DropdownSimple'
        ),
      'terms-and-conditions': () =>
        import(
          /* webpackChunkName: "terms-and-conditions" */ 'components/TermsAndConditions'
        ),
      'autocomplete-dropdown': () =>
        import(
          /* webpackChunkName: "autocomplete-with-label" */ 'components/Dropdown/AutocompleteWithLabel'
        ),
      'book-and-travel': () =>
        import(
          /* webpackChunkName: "book-and-travel" */ 'containers/Pages/LandingPage/BookAndTravel'
        ),
      'flight-item': () =>
        import(
          /* webpackChunkName: "clp-flight-item" */ 'containers/Pages/LandingPage/FlightItem'
        ),
      'histogram-legend': () =>
        import(
          /* webpackChunkName: "histogram-legend" */ 'components/Histogram/HistogramLegend'
        ),
      Origin: () =>
        import(
          /* webpackChunkName: "clp-origin-select" */ 'components/eHorizon/Destination/Origin'
        )
    },
    mixins: [ImageHelpers, OriginMixin, SearchForm],
    data() {
      return {
        originItems: [],
        filter: '',
        closedCategories: {},
        sorterOptions: [
          {
            value: 'cityAsc',
            title: this.$t('reusable.sorters.destinationAsc')
          },
          {
            value: 'cityDesc',
            title: this.$t('reusable.sorters.destinationDesc')
          }
        ]
      }
    },
    computed: {
      ...mapState('device', ['deviceType']),

      ...mapGetters('campaignLandingPrices', [
        'filteredFlights',
        'destinations',
        'airports'
      ]),
      ...mapState('campaignLandingPrices', [
        'pageData',
        'timerTitle',
        'timerEnabled',
        'deadline',
        'selectedDestin',
        'destinList',
        'selectedSorter',
        'termsAndConditions',
        'iconType',
        'clpOrigDestData',
        'hideFilterBlock',
        'hideImages',
        'isOndCampaign',
        'openAllHistograms'
      ]),

      backgroundUrl() {
        const source = {
          desktop: this.pageData.desktopImg,
          tablet: this.pageData.tabletImg,
          mobile: this.pageData.mobileImg
        }

        return source[this.deviceType]
          ? this.$_ImageHelpers_getBackgroundImage(source[this.deviceType])
          : ''
      },

      isFiltersBlockHidden() {
        return this.isXlsEmpty || this.hideFilterBlock
      },

      isXlsEmpty() {
        return !Object.keys(this.clpOrigDestData).length
      },

      separateFlightPrices() {
        return Object.keys(this.clpOrigDestData)
      },

      filteredAirports() {
        const searchResults = this.$t('searchForm.originDestin.origin.matchedOrigins')

        if (!this.airports.includes(searchResults)) {
          return this.airports
        }

        return this.airports.filter((item) => item !== searchResults)
      },

      wrongInputLangTranslation() {
        return this.$i18n.locale() === 'ru'
          ? this.$t('searchForm.originDestin.invalidLanguageEHorizonCyrillic')
          : this.$t('searchForm.originDestin.invalidLanguageEHorizon')
      }
    },

    mounted() {
      this.getDestinList()
      this.createCategoryList()
    },

    methods: {
      createCategoryList() {
        this.filteredFlights.forEach((category, index) => {
          this.$set(this.closedCategories, index, false)
        })
      },
      getDestinList() {
        let list = []
        this.destinations.forEach((el) => {
          list.push({
            title: el.city + ` (${el.code + ', ' + el.country})`,
            value: el.code
          })
        })
        this.$store.commit('campaignLandingPrices/setDestinList', list)
      },
      setDestin(destin) {
        this.$store.commit('campaignLandingPrices/setSelectedDestin', destin)
        this.filterDestinations(destin)
      },
      setOrigin(origin) {
        this.onOriginSelect(origin)
        this.getDestinList()
        this.openAllCategoties()
      },

      toggleCategory(key) {
        this.closedCategories[key] = !this.closedCategories[key]
      },

      openAllCategoties() {
        Object.keys(this.closedCategories).forEach((key) => {
          this.closedCategories[key] = false
        })
      },

      filterDestinations(filter) {
        this.filter = filter
        if (filter) {
          this.openAllCategoties()
        }
        this.$store.commit('campaignLandingPrices/setSelectedFilter', filter)
      },

      selectSorter(sorter) {
        this.$store.dispatch('campaignLandingPrices/setSelectedSorter', sorter)
      }
    }
  }
</script>

<style
  lang="scss"
  rel="stylesheet/scss"
  scoped
>
  @import '~assets/styles/common.scss';

  .main-content {
    color: $blue-dark;
    background-color: $blue-bg-css;
  }

  .top-wrap {
    background: white;
  }

  .content-block {
    @include bp-mf-tablet {
      max-width: 1120px;
      margin: 0 auto;
    }
  }

  .clp-banner {
    width: 100%;
    height: 200px;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center center;

    @include bp-tablet-only {
      height: 325px;
    }

    @include bp-mf-desktop {
      height: 375px;
    }
  }

  .top-content {
    text-align: center;
    margin: 0 20px;
    padding: 28px 0;
    border-bottom: 1px solid #d9dce1;

    @include bp-mf-desktop {
      margin: 0 auto;
    }
  }

  h1 {
    font-size: 32px;
    font-weight: 500;
    line-height: 42px;
    margin: 0 0 16px;

    @include bp-mf-tablet {
      font-size: 40px;
    }
  }

  .intro-text-block {
    font-size: 20px;
    font-weight: 300;
    line-height: 28px;

    @include bp-mf-tablet {
      padding: 0;
      line-height: 32px;
    }

    @include bp-mf-desktop {
      padding: 0 20px;
    }

    ::v-deep p {
      margin: 0;
    }

    ::v-deep strong {
      font-weight: 500;
    }

    ::v-deep a {
      color: $blue-medium;

      &:hover {
        @include bp-mf-tablet {
          color: $blue-light;
        }
      }
    }
  }

  .countdown-block {
    padding: 24px 0;
    border-bottom: 1px solid #d9dce1;

    @include bp-mf-tablet {
      padding: 28px 0 32px;
    }
  }

  .flights-block {
    margin: 20px;

    @include bp-mf-tablet {
      display: flex;
      flex-wrap: wrap;
    }

    @include bp-mf-desktop {
      margin: 20px 0;
    }

    @include bp-desktop-small {
      margin: 20px;
    }
  }

  h2 {
    color: $blue-dark;
    font-size: 24px;
    font-weight: 500;
    padding: 27px 20px 25px;
    margin: 0;
    background-color: $blue-bg-css;

    @include bp-mf-tablet {
      padding: 37px 25px 25px;
    }

    @include bp-mf-desktop {
      padding-left: 0;
    }

    @include bp-desktop-small {
      padding-left: 20px;
    }
  }

  .search-form-wrap {
    .origin {
      .clp-origin {
        min-width: 155px;
        height: 48px;
        border-radius: 3px;
        border: 1px solid $blue-border;

        @include bp-mf-tablet {
          max-width: 155px;
        }

        ::v-deep {
          .input-container {
            .placeholder {
              font-size: rem(14, 16);
              top: 22px;
              max-width: calc(100% - 24px);
              @include text-overflow();

              b {
                font-weight: 400;
              }
            }

            &.visible {
              @include bp-mf-tablet {
                z-index: 2;
                top: 10px;
                left: 10px;
              }
              @include bp-mf-desktop {
                top: 0;
                left: 0;

                input {
                  top: 12px;
                }
              }
            }
          }
          .dropdown-menu {
            @include bp-mf-tablet {
              z-index: 1;
              top: 0;
              left: 0;

              .close-icon {
                top: 10px;
                right: 10px;
              }
            }
          }
        }
      }
    }

    .search-form {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin: 0 20px;

      @include bp-mf-tablet {
        justify-content: space-between;
        align-items: center;
        flex-direction: row;
        gap: 0;
      }

      @include bp-mf-desktop {
        margin: 0;
      }
    }

    .destin {
      @include bp-mf-tablet {
        width: 100%;
        margin: 0 20px 0 12px;
      }

      ::v-deep {
        input {
          color: $blue-dark;

          @include input-placeholder {
            color: $text-secondary;
          }

          .dropdown-list {
            @include bp-mf-tablet {
              max-height: 300px;
              z-index: 1;
            }
          }
        }
      }

      .destin-dropdown {
        ::v-deep {
          .dropdown-wrap {
            input {
              text-transform: unset;
              margin-top: 0;
              color: $blue-dark;
              font-size: em(16);

              @include bp-mf-tablet {
                font-size: em(14);
              }
            }
          }

          .dropdown-list {
            width: 100%;
          }
        }
      }
    }

    .origin ::v-deep .dropdown-wrap,
    .destin ::v-deep .dropdown-wrap,
    .sorter {
      @include bp-mobile {
        height: 56px;
        border-radius: 4px;
        border: 1px solid $grey-border-light;
      }
    }

    .sorter {
      @include bp-mobile {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border: 1px solid rgba($blue-dark, 0.2);
      }

      ::v-deep .dropdown-field {
        font-weight: 500;
        background: transparent;
        border-radius: 4px;
        padding: 0;
        min-width: 32px;

        @include bp-mobile {
          border: none;
          padding-left: 16px;
        }

        @include bp-mf-tablet {
          font-size: 0;
          height: 32px;
        }

        &:after {
          @include icon-font-thin();
          font-size: 22px;
          width: 32px;
          height: 32px;
          content: $icon-sort;
          vertical-align: middle;
          transform: rotate(0) !important;
          top: calc(50% - 11px);
          right: 0;
          color: $blue-medium;

          @include bp-mf-tablet {
            right: calc(50% - 21px);
          }
        }
      }

      ::v-deep .dropdown-wrap {
        &.open .dropdown-field {
          border: 1px solid $blue-medium;
        }
      }

      ::v-deep .dropdown-list {
        min-width: 258px;
        right: 0;
        left: auto;
        top: 34px;
        max-height: none;

        li {
          font-size: 16px;
          line-height: 55px;
        }
      }
    }
  }

  ::v-deep .histogram-legend {
    display: flex;
    margin: 20px 0 0 20px !important;

    @include bp-mf-desktop {
      margin: 20px 0 0 0 !important;
    }

    .legend {
      margin-bottom: 0;
    }

    .direct {
      margin-right: 40px;
    }
  }

  .terms-and-conditions-block {
    margin: 0 !important;
    text-align: left;
    border-top: $blue-dividers;

    ::v-deep p {
      margin: 0;
    }

    @include bp-mf-desktop {
      padding: 0;
    }

    ::v-deep .terms-and-conditions-panel-title {
      margin: 28px 0 20px;
      line-height: 31px !important;
      height: auto !important;
      color: $blue-dark !important;

      &:before {
        color: rgba($blue-dark, 0.32) !important;
      }

      &:after {
        display: none !important;
      }
    }
  }

  .category {
    padding: 30px 0;
    border-bottom: 1px solid $blue-dividers;

    h2 {
      position: relative;
      cursor: pointer;

      &:after {
        content: $icon-arrow-right-thin;
        @include icon-font-thin();
        position: absolute;
        top: calc(50% - 7px);
        right: 35px;
        transform: rotate(90deg);
        text-align: center;
        font-weight: 700;
        line-height: 1;
        color: $blue-medium;

        @include bp-tablet-only {
          right: 20px;
        }

        @include bp-tablet {
          top: calc(50% - 5px);
          font-size: 12px;
        }

        @include bp-mobile {
          right: 23px;
        }
      }
    }

    &.closed {
      h2:after {
        transform: rotate(270deg);
      }
    }

    &.empty {
      padding: 0;
      border: none;

      .flights-block {
        margin: 0;
      }
    }
  }
</style>
